import {
  getAttr,
  isVoidTag,
  parseHtml,
  type HtmlAttr,
  type HtmlElement,
  type HtmlNode,
} from '@/msgdataApi/primitives/htmlDom'
import { isSafeRichUrl } from './richElements'

const SAFE_TAGS = new Set([
  'a', 'abbr', 'address', 'b', 'big', 'blockquote', 'br', 'caption', 'center',
  'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'dfn', 'div', 'dl', 'dt',
  'details', 'em', 'fieldset', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i',
  'img', 'ins', 'kbd', 'label', 'legend', 'li', 'ol', 'p', 'pre', 'q', 's',
  'object', 'samp', 'small', 'span', 'strike', 'strong', 'sub', 'sup', 'table', 'tbody',
  'td', 'tfoot', 'th', 'thead', 'tr', 'tt', 'u', 'ul', 'var', 'summary', 'user-profile',
  'ui-accordion', 'accordion-section', 'message-callout', 'progress-meter',
  'content-tabs', 'content-tab', 'current-time',
  'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
  'text', 'tspan', 'defs', 'lineargradient', 'radialgradient', 'stop', 'clippath',
])

const GLOBAL_ATTRS = new Set([
  'class', 'title', 'role', 'aria-label', 'aria-hidden', 'colspan', 'rowspan',
  'alt', 'target', 'rel', 'type', 'name', 'dir', 'lang', 'align', 'valign',
  'border', 'cellpadding', 'cellspacing', 'color', 'face', 'size', 'bgcolor',
  'width', 'height', 'viewbox', 'preserveaspectratio', 'xmlns', 'fill', 'stroke',
  'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'opacity', 'transform',
  'x', 'y', 'x1', 'x2', 'y1', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points',
  'offset', 'stop-color', 'stop-opacity', 'font-size', 'font-family', 'text-anchor',
])

const SAFE_STYLE_PROPERTIES = new Set([
  'position', 'display', 'left', 'top', 'right', 'bottom', 'width', 'height',
  'min-width', 'min-height', 'max-width', 'max-height', 'overflow', 'overflow-x',
  'overflow-y', 'margin', 'margin-left', 'margin-right', 'margin-top',
  'margin-bottom', 'padding', 'padding-left', 'padding-right', 'padding-top',
  'padding-bottom', 'color', 'background', 'background-color', 'border',
  'border-top', 'border-right', 'border-bottom', 'border-left', 'border-color',
  'border-width', 'border-style', 'border-radius', 'font-size',
  'font-family', 'font-weight', 'font-style', 'line-height', 'text-align',
  'text-decoration', 'white-space', 'word-break', 'z-index', 'transform',
  'transform-origin', 'touch-action', 'float', 'clear', 'opacity', 'cursor',
  'box-sizing', 'box-shadow', 'letter-spacing', 'text-transform', 'list-style',
  'flex', 'flex-basis', 'flex-direction', 'flex-grow', 'flex-shrink', 'flex-wrap',
  'align-content', 'align-items', 'align-self', 'justify-content', 'gap',
])

function safeStyle(value: string): string {
  return value
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .flatMap(part => {
      const colon = part.indexOf(':')
      if (colon < 1) return []
      const property = part.slice(0, colon).trim().toLowerCase()
      const raw = part.slice(colon + 1).trim()
      if (!SAFE_STYLE_PROPERTIES.has(property)) return []
      if (/(?:url\s*\(|expression\s*\(|javascript:|data:text\/html)/i.test(raw)) return []
      return [`${property}:${raw}`]
    })
    .join(';')
}

function safeAttrs(node: HtmlElement): HtmlAttr[] {
  const deskEl = getAttr(node, 'desk_el')
  const kind = getAttr(node, 'data-kind')
  const allowedData = new Set<string>()
  if (deskEl === 'rich_layout') allowedData.add('data-rich-version')
  if (deskEl === 'rich_block') allowedData.add('data-block-id')
  if (deskEl === 'rich_canvas') {
    allowedData.add('data-rich-version')
    allowedData.add('data-scene')
  }
  if (deskEl === 'rich_el' && /^(?:user-profile|ui-accordion|message-callout|progress-meter|content-tabs|current-time|content-reveal|designed-section)$/.test(kind ?? '')) allowedData.add('data-kind')
  if (deskEl === 'rich_data' && /^[a-z][a-z0-9_-]{0,40}$/.test(kind ?? '')) {
    allowedData.add('data-rich-version')
    allowedData.add('data-kind')
    allowedData.add('data-doc')
  }

  const attrs: HtmlAttr[] = []
  for (const attr of node.attrs) {
    const name = attr.name.toLowerCase()
    if (name.startsWith('on') || name === 'srcdoc') continue
    if (name === 'desk_el') {
      if (/^(?:rich_layout|rich_block|rich_canvas|rich_data|rich_el|video_el)$/.test(attr.value)) attrs.push(attr)
      continue
    }
    if (allowedData.has(name)) {
      if ((name !== 'data-scene' && name !== 'data-doc') || attr.value.length <= 2_000_000) attrs.push(attr)
      continue
    }
    if (node.name === 'user-profile' && name === 'user-id') {
      attrs.push({ name, value: attr.value.replace(/[^a-zA-Z0-9_.:-]/g, '').slice(0, 80) })
      continue
    }
    if (node.name === 'accordion-section' && name === 'title') {
      attrs.push({ name, value: attr.value.slice(0, 160) })
      continue
    }
    if (node.name === 'accordion-section' && name === 'open') {
      attrs.push({ name, value: '' })
      continue
    }
    if (node.name === 'message-callout' && name === 'tone') {
      attrs.push({ name, value: /^(?:info|success|warning|danger)$/.test(attr.value) ? attr.value : 'info' })
      continue
    }
    if (node.name === 'message-callout' && name === 'title') {
      attrs.push({ name, value: attr.value.slice(0, 160) })
      continue
    }
    if (node.name === 'progress-meter' && name === 'label') {
      attrs.push({ name, value: attr.value.slice(0, 160) })
      continue
    }
    if (node.name === 'progress-meter' && (name === 'value' || name === 'max')) {
      const number = Number(attr.value)
      if (Number.isFinite(number)) attrs.push({ name, value: String(Math.max(name === 'max' ? 1 : 0, Math.min(1_000_000, number))) })
      continue
    }
    if (node.name === 'content-tab' && name === 'title') {
      attrs.push({ name, value: attr.value.slice(0, 160) })
      continue
    }
    if (node.name === 'content-tab' && name === 'active') {
      attrs.push({ name, value: '' })
      continue
    }
    if (name === 'style') {
      const value = safeStyle(attr.value)
      if (value) attrs.push({ name, value })
      continue
    }
    if (name === 'href' || name === 'src' || name === 'data' || name === 'xlink:href') {
      if (isSafeRichUrl(attr.value)) attrs.push(attr)
      continue
    }
    if (GLOBAL_ATTRS.has(name)) attrs.push(attr)
  }
  return attrs
}

function sanitizeNode(node: HtmlNode): HtmlNode[] {
  if (node.kind === 'text') return [node]
  if (node.kind === 'comment') return []
  node.name = node.name.toLowerCase()
  const children = node.children.flatMap(sanitizeNode)
  if (!SAFE_TAGS.has(node.name)) return children
  node.attrs = safeAttrs(node)
  node.children = children
  return [node]
}

function escapeSafeText(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

function escapeSafeAttr(value: string): string {
  return escapeSafeText(value)
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function serializeSafeNodes(nodes: HtmlNode[]): string {
  let html = ''
  for (const node of nodes) {
    if (node.kind === 'text') {
      html += escapeSafeText(node.value)
      continue
    }
    if (node.kind === 'comment') continue
    const attrs = node.attrs
      .map(attr => ` ${attr.name}="${escapeSafeAttr(attr.value)}"`)
      .join('')
    html += `<${node.name}${attrs}>`
    if (isVoidTag(node.name)) continue
    html += serializeSafeNodes(node.children)
    html += `</${node.name}>`
  }
  return html
}

export function sanitizeRichContentHtml(html: string): string {
  if (!html.trim()) return ''
  const doc = parseHtml(html)
  doc.leading = []
  doc.head = []
  doc.body = doc.body.flatMap(sanitizeNode)
  return serializeSafeNodes(doc.body)
}

export function richHtmlToPlainText(html: string): string {
  const doc = parseHtml(html)
  const chunks: string[] = []
  const blockTags = new Set(['div', 'p', 'li', 'blockquote', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
  const visit = (node: HtmlNode) => {
    if (node.kind === 'text') {
      chunks.push(node.value)
      return
    }
    if (node.kind === 'comment') return
    if (node.name === 'br') chunks.push('\n')
    node.children.forEach(visit)
    if (blockTags.has(node.name)) chunks.push('\n')
  }
  doc.body.forEach(visit)
  return chunks.join('').replace(/\n{3,}/g, '\n\n').trimEnd()
}
